'use server'

import { apiRequest, ApiResponse } from '../request'

export interface DashboardOverview {
    totalLeads: number
    totalDeals: number
    openDeals: { count: number; value: number }
    wonDeals: { count: number; value: number }
    lostDeals: { count: number; value: number }
    conversionRate: number
    avgDealValue: number
    avgTimeToClose: number
    comparison: {
        leadsChange: number
        revenueChange: number
        dealsChange: number
    }
}

export interface PipelineStageMetrics {
    id: number
    name: string
    color: string | null
    sortOrder: number
    probability: number
    dealCount: number
    totalValue: number
    weightedValue: number
    avgTimeInStage: number
}

export interface PipelineMetrics {
    pipeline: { id: number; name: string }
    stages: PipelineStageMetrics[]
}

export interface Forecast {
    bestCase: number
    mostLikely: number
    committed: number
    closed: number
    byMonth: { month: string; projected: number; closed: number }[]
}

export interface VelocityStage {
    id: number
    name: string
    color: string | null
    avgDays: number
    dealCount: number
}

export interface Velocity {
    avgTotalDays: number
    stages: VelocityStage[]
    fastest: { dealId: number; title: string; days: number } | null
    slowest: { dealId: number; title: string; days: number } | null
}

export interface LeaderboardEntry {
    userId: number
    userName: string
    wonDeals: number
    totalValue: number
    conversionRate: number
    avgDaysToClose: number
}

export interface ConversionStage {
    id: number
    name: string
    color: string | null
    entered: number
    advanced: number
    conversionRate: number
}

export interface SentimentDistribution {
    distribution: { sentiment: string; count: number; percentage: number }[]
    trend: { date: string; sentiments: Record<string, number> }[]
}

export interface ActivityData {
    data: {
        period: string
        leadsCreated: number
        dealsCreated: number
        dealsWon: number
        dealsLost: number
    }[]
}

// API Functions
export async function getDashboardOverview(params?: { startDate?: string; endDate?: string; pipelineId?: number }): Promise<ApiResponse<DashboardOverview>> {
    return apiRequest<DashboardOverview>('sales/dashboard/overview', { params })
}

export async function getPipelineMetrics(pipelineId: number, params?: { startDate?: string; endDate?: string }): Promise<ApiResponse<PipelineMetrics>> {
    return apiRequest<PipelineMetrics>(`sales/dashboard/pipeline/${pipelineId}`, { params })
}

export async function getForecast(params?: { pipelineId?: number }): Promise<ApiResponse<Forecast>> {
    return apiRequest<Forecast>('sales/dashboard/forecast', { params })
}

export async function getVelocity(params?: { pipelineId?: number }): Promise<ApiResponse<Velocity>> {
    return apiRequest<Velocity>('sales/dashboard/velocity', { params })
}

export async function getLeaderboard(params?: { startDate?: string; endDate?: string }): Promise<ApiResponse<{ rankings: LeaderboardEntry[] }>> {
    return apiRequest<{ rankings: LeaderboardEntry[] }>('sales/dashboard/leaderboard', { params })
}

export async function getConversionRates(pipelineId: number): Promise<ApiResponse<{ stages: ConversionStage[] }>> {
    return apiRequest<{ stages: ConversionStage[] }>('sales/dashboard/conversion', { params: { pipelineId } })
}

export async function getSentimentDistribution(params?: { startDate?: string; endDate?: string }): Promise<ApiResponse<SentimentDistribution>> {
    return apiRequest<SentimentDistribution>('sales/dashboard/sentiment', { params })
}

export async function getActivity(params?: { startDate?: string; endDate?: string; groupBy?: string }): Promise<ApiResponse<ActivityData>> {
    return apiRequest<ActivityData>('sales/dashboard/activity', { params })
}
